import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import acerbisLogo from './../../../assets/images/acerbis-mission-logo.png';
import elephantLogo from './../../../assets/images/elephant-logo.png';
import expeditionImage from './../../../assets/images/mission.jpg';

export interface MissionCardData {
  title: string;
  description: string;
  logos?: { src: string; alt: string }[];
}

@customElement('rftw-mission')
export class RftwMission extends LitElement {
  /** Section small header tag */
  @property({ type: String }) sectionTag = 'NUESTRA MISIÓN';

  /** Main section title */
  @property({ type: String }) title = 'Ride for the Wild';

  /** Main mission description paragraphs */
  @property({ type: String }) paragraph1 =
    'La idea es simple: crear diferentes rides por todo Costa Rica, y usar las ganancias obtenidas para apoyar distintas causas que ayudan a la fauna del país. Cada edición visita una región nueva y elige un refugio o proyecto de conservación al que apoyar.';

  @property({ type: String }) paragraph2 =
    'Esta edición es posible gracias a la alianza con Acerbis Costa Rica y Elephant Moto Tours Costa Rica, que acompaña a los pilotos y respalda la causa.';

  /** Grid card items */
  @property({ type: Array }) cards: MissionCardData[] = [
    {
      title: 'Edición 01: Nosara',
      description:
        'Primera ruta recorre los trillos de Guanacaste. Fecha: 14 y 15 de Noviembre',
    },
    {
      title: 'IAR Nosara',
      description:
        'International Animal Rescue, se fundó hace 20 años con el objetivo de ayudar a proteger la fauna silvestre de Costa Rica.',
    },
    {
      title: 'Acerbis y Elephant Costa Rica',
      description: 'Aliado técnico y de equipamiento para el ride.',
      logos: [
        { src: acerbisLogo, alt: 'Acerbis Costa Rica' },
        { src: elephantLogo, alt: 'Elephant Moto Tours Costa Rica' },
      ],
    },
  ];

  static styles = css`
    :host {
      display: block;
      width: 100%;
      box-sizing: border-box;
    }

    .mission-container {
      background-color: var(--wa-color-surface-default, #f8f6f0);
      padding: 4.5rem 2rem;
      box-sizing: border-box;
    }

    .content-wrapper {
      max-width: 1200px;
      margin: 0 auto;
    }

    .expedition-card {
      display: grid;
      grid-template-columns: 1fr 1fr;
      overflow: hidden;
      margin-bottom: 3rem;
      border-radius: 8px;
      background: #1e251c;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    .expedition-image {
      width: 100%;
      height: 100%;
      min-height: 20rem;
      object-fit: cover;
      object-position: center;
      display: block;
    }

    .expedition-content {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      padding: 2.25rem;
      color: #ffffff;
    }

    .expedition-tag {
      margin: 0 0 0.75rem;
      color: #65a07c;
      font-size: 0.7rem;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }

    .expedition-title {
      margin: 0 0 0.75rem;
      font-size: clamp(1.7rem, 3vw, 2.25rem);
      line-height: 1.15;
    }

    .expedition-description {
      margin: 0 0 1.5rem;
      color: #c5c9c1;
      font-size: 0.9rem;
      line-height: 1.5;
    }

    .expedition-details {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 1rem;
      margin: 0 0 1.75rem;
    }

    .detail-label,
    .detail-value {
      display: block;
    }

    .detail-label {
      margin-bottom: 0.35rem;
      color: #abb0a8;
      font-size: 0.75rem;
    }

    .detail-value {
      color: #ffffff;
      font-size: 0.85rem;
      font-weight: 700;
    }

    .expedition-action {
      min-height: 2.75rem;
      border: 0;
      border-radius: 6px;
      background: var(--wa-color-orange-50, #ff4500);
      color: #ffffff;
      cursor: pointer;
      font: inherit;
      font-size: 0.9rem;
      font-weight: 700;
    }

    .expedition-action:hover,
    .expedition-action:focus-visible {
      background: #e03d00;
      outline: none;
    }

    .section-tag {
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--wa-color-orange-50, #d95328);
      margin-bottom: 0.5rem;
    }

    .section-title {
      font-size: clamp(2rem, 4vw, 2.75rem);
      font-weight: 800;
      color: #1b2e23;
      margin: 0 0 1.5rem 0;
      letter-spacing: -0.02em;
    }

    .description-group {
      max-width: 720px;
      margin-bottom: 3.5rem;
    }

    .description-group p {
      font-size: 0.98rem;
      line-height: 1.65;
      color: var(--wa-color-text-normal, #4a5568);
      margin: 0 0 1rem 0;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .description-group p:last-child {
      margin-bottom: 0;
    }

    /* 3-Column Cards Grid */
    .cards-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 1.5rem;
    }

    .card {
      background-color: #ffffff;
      border-radius: 12px;
      padding: 1.75rem 1.5rem;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.03);
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
    }

    .card-logos {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 1rem;
      margin: auto 0 0;
    }

    .card-logo {
      width: min(8rem, 45%);
      height: 2rem;
      object-fit: contain;
      object-position: right center;
    }

    .card:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
    }

    .card-title {
      font-size: 1.15rem;
      font-weight: 700;
      color: #1b2e23;
      margin: 0 0 0.75rem 0;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .card-desc {
      font-size: 0.875rem;
      line-height: 1.55;
      color: var(--wa-color-text-normal, #4a5568);
      margin: 0;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    /* Mobile Responsive Layout */
    @media (max-width: 900px) {
      .expedition-card {
        grid-template-columns: 1fr;
      }

      .expedition-image {
        min-height: 0;
        aspect-ratio: 16 / 9;
      }

      .cards-grid {
        grid-template-columns: 1fr;
        gap: 1rem;
      }
    }

    @media (max-width: 600px) {
      .mission-container {
        padding: 3rem 1.25rem;
      }

      .description-group {
        margin-bottom: 2.5rem;
      }

      .expedition-card {
        margin-bottom: 2.5rem;
      }

      .expedition-content {
        padding: 1.5rem 1.25rem;
      }

      .expedition-details {
        grid-template-columns: repeat(2, 1fr);
        row-gap: 1.25rem;
      }

      .expedition-action {
        min-height: 3rem;
      }

      .card {
        padding: 1.25rem;
      }

      .card-logo {
        width: min(8rem, 45%);
      }

      .card-logos {
        margin-top: 1.5rem;
      }
    }
  `;

  render() {
    return html`
      <section class="mission-container" aria-label="Nuestra Misión">
        <div class="content-wrapper">
          <article class="expedition-card" aria-label="Expedición Guanacaste">
            <img
              class="expedition-image"
              src=${expeditionImage}
              alt="Monos rescatados por IAR Nosara"
              decoding="async"
            />
            <div class="expedition-content">
              <p class="expedition-tag">Inscripciones abiertas</p>
              <h2 class="expedition-title">Expedición Guanacaste</h2>
              <p class="expedition-description">
                Una ruta off-road por los trillos de Guanacaste que recauda
                fondos para el rescate y la atención de fauna silvestre.
              </p>
              <div class="expedition-details">
                <div>
                  <span class="detail-label">Ubicación</span>
                  <strong class="detail-value">Nosara, CR</strong>
                </div>
                <div>
                  <span class="detail-label">Duración</span>
                  <strong class="detail-value">2 días</strong>
                </div>
                <div>
                  <span class="detail-label">Fecha</span>
                  <strong class="detail-value">14-15 Nov</strong>
                </div>
                <div>
                  <span class="detail-label">Costo</span>
                  <strong class="detail-value">$50</strong>
                </div>
              </div>
              <button class="expedition-action">Inscribirme al ride</button>
            </div>
          </article>

          ${this.sectionTag
            ? html`<div class="section-tag">${this.sectionTag}</div>`
            : ''}

          <h2 class="section-title">${this.title}</h2>

          <div class="description-group">
            ${this.paragraph1 ? html`<p>${this.paragraph1}</p>` : ''}
            ${this.paragraph2 ? html`<p>${this.paragraph2}</p>` : ''}
          </div>

          <div class="cards-grid">
            ${this.cards.map(
              (card) => html`
                <div class="card">
                  <h3 class="card-title">${card.title}</h3>
                  <p class="card-desc">${card.description}</p>
                  ${card.logos?.length
                    ? html`<div class="card-logos">
                        ${card.logos.map(
                          (logo) =>
                            html`<img
                              class="card-logo"
                              src=${logo.src}
                              alt=${logo.alt}
                              loading="lazy"
                              decoding="async"
                            />`
                        )}
                      </div>`
                    : ''}
                </div>
              `
            )}
          </div>
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rftw-mission': RftwMission;
  }
}

